/**
 * 編集ボタンに関する関数群 ====================================================================================
 */

import * as constants from "./constants";
import * as tools from "./tools";
import * as operationPosition from "./operationPosition";
import * as generator from "./generator";
import * as colorToolbox from "../../../image/utils/colorToolbox";
import * as createjs from "createjs-module";

// 処理対象
let stage;
let displayObjectsContainer;
let operationButtonsContainer;
let buttonFrameShape;
let previewContainer;
let focusObject;

// TODO: 全体的にセーブロードのためのJSONオブジェクトと連携させる必要がある

/**
 * 各種ボタンを作成する
 * @param  {} stage
 */
export const initButtons = (targetStage) => {
  stage = targetStage;
  displayObjectsContainer = stage.getChildByName("displayObjectsContainer");
  previewContainer = stage.getChildByName("previewContainer");

  // ボタンコンテナの生成
  operationButtonsContainer = new createjs.Container();
  // stageから参照できるように
  operationButtonsContainer.name = "operationButtonsContainer";
  operationButtonsContainer.visible = false;
  stage.addChild(operationButtonsContainer);

  // ボタンを表示する枠の生成
  createButtonFrameShape();

  // ボタン類の生成
  for (const propertyName in constants.operationButtonPropertyMap) {
    createButton(constants.operationButtonPropertyMap[propertyName]);
  }
};

/**
 * 各種ボタン表示枠のサイズを補正する
 * @param  {} buttonRect
 */
const resizeButtonContainer = (buttonRect) => {
  if (buttonRect.width > constants.buttonMaxSize) {
    buttonRect.x += (buttonRect.width - constants.buttonMaxSize) / 2;
    buttonRect.width = constants.buttonMaxSize;
  } else if (buttonRect.width < constants.buttonMinSize) {
    buttonRect.x += (buttonRect.width - constants.buttonMinSize) / 2;
    buttonRect.width = constants.buttonMinSize;
  }
  if (buttonRect.height > constants.buttonMaxSize) {
    buttonRect.y += (buttonRect.height - constants.buttonMaxSize) / 2;
    buttonRect.height = constants.buttonMaxSize;
  } else if (buttonRect.height < constants.buttonMinSize) {
    buttonRect.y += (buttonRect.height - constants.buttonMinSize) / 2;
    buttonRect.height = constants.buttonMinSize;
  }
  return buttonRect;
};

/**
 * 各種ボタンを表示する
 */
const showButtons = () => {
  operationButtonsContainer.visible = true;
  let buttonRect = {
    x: focusObject.getTransformedBounds().x,
    y: focusObject.getTransformedBounds().y,
    width: focusObject.getTransformedBounds().width,
    height: focusObject.getTransformedBounds().height,
  };
  buttonRect = resizeButtonContainer(buttonRect);
  operationButtonsContainer.setBounds(
    buttonRect.x,
    buttonRect.y,
    buttonRect.width,
    buttonRect.height
  );

  for (const buttonContainer of operationButtonsContainer.children) {
    // 枠線も含まれているので別処理
    if (buttonContainer.name === "buttonFrameShape") {
      buttonContainer.graphics.clear();
      buttonContainer.graphics
        .beginStroke("#FC8350")
        .setStrokeStyle(5)
        .drawRect(
          buttonRect.x,
          buttonRect.y,
          buttonRect.width,
          buttonRect.height
        );
      // 四角に当たり判定
      const hitAreaShape = new createjs.Shape();
      hitAreaShape.graphics
        .beginFill("#FFF")
        .drawRect(
          buttonRect.x,
          buttonRect.y,
          buttonRect.width,
          buttonRect.height
        );
      buttonContainer.hitArea = hitAreaShape;
      continue;
    }
    // 色変更ボタンは表示しないタイプがある
    if (
      buttonContainer.name === "changeColor" &&
      focusObject.decoData.type == constants.decoType.normalImage
    ) {
      buttonContainer.visible = false;
      continue;
    } else {
      buttonContainer.visible = true;
    }
    const buttonPosition =
      constants.operationButtonPositions[buttonContainer.buttonPositionNo];
    buttonContainer.x =
      operationButtonsContainer.getBounds().x +
      operationButtonsContainer.getBounds().width * buttonPosition.x;
    buttonContainer.y =
      operationButtonsContainer.getBounds().y +
      operationButtonsContainer.getBounds().height * buttonPosition.y;
    if (
      buttonContainer.showFunction !== undefined &&
      focusObject.decoData.color !== undefined
    ) {
      buttonContainer.showFunction(focusObject.decoData.color);
    }
  }
};

/**
 * 各種ボタンを非表示にする
 */
const hideButtons = () => {
  operationButtonsContainer.visible = false;
};

/**
 * 編集ボタンのベースコンテナを返す関数
 * @param  {} property
 */
const createButton = (property) => {
  const buttonContainer = new createjs.Container();
  buttonContainer.name = property.buttonName;

  // 色変更ボタンだけ特殊な構造
  if (property.buttonName === "changeColor") {
    // XXX buttonContainer.buttonPositionX,Y使えている？
    buttonContainer.buttonPositionX = property.buttonPositionX;
    buttonContainer.buttonPositionY = property.buttonPositionY;
    // 色変更しない土台部分
    const baseImage = new createjs.Bitmap(property.baseImageSrc);
    baseImage.image.onload = () => {
      // anchorを左上から中心に変更
      baseImage.regX = baseImage.getBounds().width / 2;
      baseImage.regY = baseImage.getBounds().height / 2;
      buttonContainer.addChild(baseImage);

      // 当たり判定領域の計算（透明部分は無視されてしまうので）
      tools.setHitArea(buttonContainer);

      // 色が変わる部分
      const circle = new createjs.Shape();
      circle.name = "circle";
      circle.graphics.beginFill("#00BBFF").drawCircle(0, 0, 33);
      buttonContainer.addChild(circle);

      // 文字部分
      const label = new createjs.Bitmap(property.labelImageSrc);
      label.image.onload = () => {
        // anchorを左上から中心に変更
        label.regX = label.getBounds().width / 2;
        label.regY = label.getBounds().height / 2;
        buttonContainer.addChild(label);
      };

      // 表示時に色変更する関数を登録しておく
      const showFunction = (color) => {
        circle.graphics.clear();
        circle.graphics.beginFill(color).drawCircle(0, 0, 33);
      };
      buttonContainer["showFunction"] = showFunction;
    };
  } else {
    buttonContainer.buttonPositionNo = property.buttonPositionNo;
    // 色が変わる部分
    const circle = new createjs.Shape();
    circle.name = "circle";
    circle.graphics.beginFill(property.buttonColor).drawCircle(0, 0, 33);
    buttonContainer.addChild(circle);
    const label = new createjs.Text(
      property.labelText,
      "16px sans-serif",
      "#ffffff"
    );
    // anchorを左上から中心に変更
    label.regX = label.getBounds().width / 2;
    label.regY = label.getBounds().height / 2 + property.textOffsetY;
    buttonContainer.addChild(label);
    const iconUnicode = property.buttonIcon;
    const iconInt = parseInt(iconUnicode, 16);
    const icon = new createjs.Text(
      String.fromCharCode(iconInt),
      "30px icomoon-ui",
      "#ffffff"
    );
    // anchorを左上から中心に変更
    icon.regX = icon.getBounds().width / 2;
    icon.regY = icon.getBounds().height / 2 - property.iconOffsetY;
    buttonContainer.addChild(icon);

    // 当たり判定領域の計算（透明部分は無視されてしまうので）
    tools.setHitArea(buttonContainer);
  }

  const buttonOperations = operationFunctions[property.functionName]();

  // 動作の登録
  if (buttonOperations.handleClick !== undefined) {
    buttonContainer.addEventListener("click", buttonOperations.handleClick);
  }
  if (buttonOperations.handleDown !== undefined) {
    buttonContainer.addEventListener("mousedown", buttonOperations.handleDown);
  }
  if (buttonOperations.handleMove !== undefined) {
    buttonContainer.addEventListener("pressmove", buttonOperations.handleMove);
  }
  if (buttonOperations.handleUp !== undefined) {
    buttonContainer.addEventListener("pressup", buttonOperations.handleUp);
  }

  operationButtonsContainer.addChild(buttonContainer);

  return buttonContainer;
};

const operationFunctions = {};

/**
 * 削除ボタンの挙動
 */
const getDeleteButtonOperations = () => {
  const operations = {};

  const handleClick = (event) => {
    deleteDisplayObject();
  };
  operations["handleClick"] = handleClick;

  return operations;
};
operationFunctions["getDeleteButtonOperations"] = getDeleteButtonOperations;

/**
 * リセットボタンの挙動（押下時、初期位置に戻る）
 */
const getResetButtonOperations = () => {
  const operations = {};

  const handleClick = (event) => {
    // rotationの初期化
    focusObject.rotation = 0;

    // 大きさ変更
    focusObject.scaleX = focusObject.scaleY = operationPosition.getDefaultScale(
      focusObject,
      focusObject.decoData.type
    );

    // 中央に配置
    operationPosition.setPositionDefault(stage, focusObject);

    focusOn();
  };
  operations["handleClick"] = handleClick;

  return operations;
};
operationFunctions["getResetButtonOperations"] = getResetButtonOperations;

/**
 * コピーボタンの挙動（押下時、対象オブジェクトから少し位置をズラして配置させる）
 */
const getCopyButtonOperations = () => {
  const operations = {};
  const copyPositionDiff = 40;

  const handleClick = (event) => {
    const displayParameter = {
      X: Math.min(focusObject.x + copyPositionDiff, stage.canvas.width),
      Y: Math.min(focusObject.y + copyPositionDiff, stage.canvas.height),
      scale: focusObject.scaleX, // scaleYと同一である想定
      rotation: focusObject.rotation,
    };
    // コピー後のcallbackにも通す
    generator.createMaterial(stage, focusObject, (stage, displayObject) => {
      copyCallback(stage, displayObject, displayParameter);
    });
  };
  operations["handleClick"] = handleClick;

  const copyCallback = (stage, displayObject, displayParameter) => {
    // displayObjectの対象は新しく作ったものに移す
    addDisplayObjectEvent(stage, displayObject);

    // rotationの設定
    displayObject.rotation = displayParameter.rotation;

    // 大きさの設定
    displayObject.scaleX = displayObject.scaleY = displayParameter.scale;

    // 位置調整
    operationPosition.setPositionDesignation(displayObject, displayParameter);

    // コピー先にfocusがあたるようにしておく
    focusOn(displayObject);
  };

  return operations;
};
operationFunctions["getCopyButtonOperations"] = getCopyButtonOperations;

/**
 * 「後面へ」ボタンの挙動
 */
const getBehindButtonOperations = () => {
  const operations = {};
  const handleClick = (event) => {
    const currentIndex = displayObjectsContainer.getChildIndex(focusObject);
    if (currentIndex != 1) {
      displayObjectsContainer.setChildIndex(focusObject, currentIndex - 1);
    }
  };
  operations["handleClick"] = handleClick;

  return operations;
};
operationFunctions["getBehindButtonOperations"] = getBehindButtonOperations;

/**
 * 「前面へ」ボタンの挙動
 */
const getFrontButtonOperations = () => {
  const operations = {};

  const handleClick = (event) => {
    const currentIndex = displayObjectsContainer.getChildIndex(focusObject);
    displayObjectsContainer.setChildIndex(focusObject, currentIndex + 1);
  };
  operations["handleClick"] = handleClick;

  return operations;
};
operationFunctions["getFrontButtonOperations"] = getFrontButtonOperations;

/**
 * 拡大縮小ボタンの挙動
 */
const getZoomButtonOperations = () => {
  const operations = {};
  let handleDownZoomParameter;
  let zoomStartScale;

  const handleDown = (event) => {
    zoomStartScale = focusObject.scaleX; // scaleYと同値の想定
    handleDownZoomParameter = calcZoomParameter(stage.mouseX, stage.mouseY);
    focusObject.alpha = 0.5;
    hideButtons();
  };
  operations["handleDown"] = handleDown;

  const handleMove = (event) => {
    const handleCurrentZoomParameter = calcZoomParameter(
      stage.mouseX,
      stage.mouseY
    );
    const targetScale =
      (zoomStartScale * handleCurrentZoomParameter) / handleDownZoomParameter;

    setScale(focusObject, targetScale);
  };
  operations["handleMove"] = handleMove;

  const handleUp = (event) => {
    focusObject.alpha = 1.0;
    showButtons();
  };
  operations["handleUp"] = handleUp;

  const normalVectorX = Math.SQRT1_2;
  const normalVectorY = Math.SQRT1_2;

  // 中心座標を通る斜め線との距離を出す（計算としては内積）
  // 斜め線より内側にあれば縮小・外側にあれば拡大
  const calcZoomParameter = (globalPointX, globalPointY) => {
    return (
      (globalPointX - focusObject.x) * normalVectorX +
      (globalPointY - focusObject.y) * normalVectorY
    );
  };

  return operations;
};
operationFunctions["getZoomButtonOperations"] = getZoomButtonOperations;

/**
 * 回転ボタンを作成する。
 */
const getRotationButtonOperations = () => {
  const operations = {};

  let handleDownDegree;
  let rotateStartDegree;

  operations["handleDown"] = (event) => {
    rotateStartDegree = focusObject.rotation;
    handleDownDegree = calcDegree(stage.mouseX, stage.mouseY);
    focusObject.alpha = 0.5;
    hideButtons();
  };

  operations["handleMove"] = (event) => {
    const handleCurrentDegree = calcDegree(stage.mouseX, stage.mouseY);

    focusObject.rotation =
      rotateStartDegree + (handleCurrentDegree - handleDownDegree);
  };

  operations["handleUp"] = (event) => {
    focusObject.alpha = 1.0;
    showButtons();
  };

  const calcDegree = (globalPointX, globalPointY) => {
    return (
      (Math.atan2(globalPointY - focusObject.y, globalPointX - focusObject.x) *
        180) /
      Math.PI
    );
  };

  return operations;
};
operationFunctions["getRotationButtonOperations"] = getRotationButtonOperations;

/**
 * 色変更ボタンを作成する。
 */
const getChangeColorButtonOperations = () => {
  const operations = {};
  let previewObject;

  const getTextColor = (target) => {
    let color;
    // タブのdata-targetからdisplayObjectに設定されている対象の色を取得する関数
    if (previewObject.decoData.type !== constants.decoType.outlineText) {
      color = previewObject.color;
    } else {
      switch (target) {
        case "text-color":
          color = previewObject.getChildByName("innerText").color;
          break;
        case "border-color":
          color = previewObject.getChildByName("outlineText").color;
          break;
      }
    }
    return color.toString().substr(1);
  };

  const changeTextColor = (toolbox, unsharpedColor) => {
    if (previewObject !== undefined) {
      const colorTarget = toolbox
        .find(".tab-menu__change-text-color.is-select")
        .data("target");
      const color = "#" + unsharpedColor;
      switch (colorTarget) {
        case "text-color":
          if (previewObject.decoData.type === constants.decoType.outlineText) {
            previewObject.getChildByName("innerText").color = color;
          }
          previewObject.color = color;
          previewObject.decoData.color = color;
          break;
        case "border-color":
          if (previewObject.decoData.type === constants.decoType.outlineText) {
            previewObject.getChildByName("outlineText").color = color;
            previewObject.decoData.outlineColor = color;
          }
          break;
      }
      colorToolbox.changeColorchip(unsharpedColor, toolbox);
    }
  };

  const handleClick = (event) => {
    if (focusObject === undefined) {
      return;
    }
    // 操作対象のオブジェクトをpreviewにコピー
    previewObject = focusObject.clone(true);
    // decoDataもclone
    previewObject.decoData = {};
    Object.assign(previewObject.decoData, focusObject.decoData);
    previewContainer.addChild(previewObject);
    focusObject.visible = false;
    // 既存のイベント登録が残っているかもしれないので、すべて外す
    const changeTextColorToolbox = $(".toolbox--change-text-color");
    const changeMaterialColorToolbox = $(".toolbox--change-material-color");
    changeTextColorToolbox.find(".colorchip").off("click");
    changeMaterialColorToolbox.find(".colorchip").off("click");
    let currentColor;
    let currentToolbox;
    switch (previewObject.decoData.type) {
      case constants.decoType.normalText:
        currentToolbox = changeTextColorToolbox;
        colorToolbox.showBorderTab(false, changeTextColorToolbox, getTextColor);
        currentColor = getTextColor(
          changeTextColorToolbox
            .find(".tab-menu__item.is-select")
            .data("target")
        );
        displayChangeColorToolbox(
          changeTextColorToolbox,
          currentColor,
          changeTextColor,
          closeChangeColorToolbox
        );
        break;
      case constants.decoType.outlineText:
        currentToolbox = changeTextColorToolbox;
        colorToolbox.showBorderTab(true, changeTextColorToolbox, getTextColor);
        currentColor = getTextColor(
          changeTextColorToolbox
            .find(".tab-menu__item.is-select")
            .data("target")
        );
        displayChangeColorToolbox(
          changeTextColorToolbox,
          currentColor,
          changeTextColor,
          closeChangeColorToolbox
        );
        break;
      case constants.decoType.monoImage:
        currentToolbox = changeMaterialColorToolbox;
        currentColor = previewObject.color.toString().substr(1);
        displayChangeColorToolbox(
          changeMaterialColorToolbox,
          currentColor,
          (toolbox, unsharpedColor) => {
            changeMaterialColor(toolbox, unsharpedColor, previewObject);
          },
          closeChangeColorToolbox
        );
        break;
      default:
        break;
    }
    // 変更ボタンを決定したときの挙動
    $(".material-change-color-btn").removeClass("is-hidden");
    $(".material-change-color-btn").off("click");
    $(".material-change-color-btn").on("click", function () {
      swapPreviewMaterial();
      closeChangeColorToolbox();
      currentToolbox.removeClass("is-visible");
      currentToolbox.addClass("is-hidden");
      focusOn();
    });
  };
  operations["handleClick"] = handleClick;

  const closeChangeColorToolbox = () => {
    $(".material-change-color-btn").addClass("is-hidden");
    $(".material-change-color-btn").off("click");
    focusObject.visible = true;
  };

  return operations;
};
operationFunctions[
  "getChangeColorButtonOperations"
] = getChangeColorButtonOperations;

/**
 * 対象オブジェクトの作成素材色を変更する
 * @param  {} toolbox
 * @param  {} unsharpedColor（#の付いていない色 ex)ffffff）
 * @param  {} targetObject
 */
export const changeMaterialColor = (toolbox, unsharpedColor, targetObject) => {
  if (targetObject !== undefined) {
    const color = "#" + unsharpedColor;
    targetObject.color = color;
    targetObject.decoData.color = color;
    colorToolbox.changeColorchip(unsharpedColor, toolbox);
    return;
  }
};

/**
 * 色変更ツールボックスを表示する（色を変えられる作成素材配置機能と統一するためにexport）
 * @param  {} toolbox
 * @param  {} currentColor（ツールボックスを開いたときの初期色）
 * @param  {} colorchipFunction（colorchipをクリックしたときに呼ぶ関数）
 * @param  {} closeCallback（ツールボックスを閉じたときのcallback）
 */
export const displayChangeColorToolbox = (
  toolbox,
  currentColor,
  colorchipFunction,
  closeCallback
) => {
  toolbox.removeClass("is-hidden");
  toolbox.addClass("is-visible");
  // 開いたときの選択色反映
  colorToolbox.changeColorchip(currentColor, toolbox);
  toolbox.find(".colorchip").off("click");
  (toolbox.find(".colorchip") as JQuery<HTMLElement>).on("click", function () {
    colorchipFunction(toolbox, $(this).data("target"));
  });
  toolbox.find(".btn-close").off("click");
  toolbox.find(".btn-close").on("click", function () {
    closeToolbox(toolbox, closeCallback);
  });
};

export const closeToolbox = function (toolbox, closeCallback = null) {
  toolbox.addClass("is-hidden");
  toolbox.removeClass("is-visible");
  // プレビューの削除
  previewContainer.removeAllChildren();
  // 次のためにテキストエリアを消しておく
  $('textarea[name="material-text"]').val("");
  // 次のために「追加」ボタンを消しておく
  $(".material-add-btn").addClass("is-hidden");
  if (closeCallback) {
    closeCallback();
  }

  $(".container").removeClass("is-unscrollable");
};

/**
 * rectから最大値・最小値を考慮して補正したscaleを返す
 * （x,y方向どちらにも同じ値に制限されることが前提）
 * @param  {} scale
 * @param  {} rect
 * @param  {} min
 * @param  {} max
 */
const getLimitedScale = (scale, rect, min, max) => {
  const scaleMaxWidth = max / rect.width;
  const scaleMaxHeight = max / rect.height;
  const scaleMinWidth = min / rect.width;
  const scaleMinHeight = min / rect.height;

  scale = Math.min(scaleMaxWidth, scaleMaxHeight, scale);
  return Math.max(Math.min(scaleMinWidth, scaleMinHeight), scale);
};

/**
 * displayObjectに最大倍率・最小倍率を考慮しながらscaleを設定する
 * @param  {} targetDisplayObject
 * @param  {} targetScale
 */
export const setScale = (targetDisplayObject, targetScale) => {
  targetDisplayObject.scaleX = targetDisplayObject.scaleY = getLimitedScale(
    targetScale,
    targetDisplayObject.getBounds(),
    constants.decoMinSize,
    constants.decoMaxSize
  );
};

/**
 * displayObjectにピンチイン・ピンチアウトイベントを登録する関数
 * @param  {} displayObject
 * @param  {} scaleFunction
 */
const createDisplayObjectPinchEvent = (displayObject, scaleFunction) => {
  let zoomStartScale;
  let touchStartInterval;

  displayObject.pinchStart = (event) => {
    zoomStartScale = displayObject.scaleX; // scaleYと同値の想定
    // ピンチインアウトを実現
    const touchesEvents = event.nativeEvent.touches;
    if (touchesEvents && touchesEvents.length > 1) {
      touchStartInterval = Math.sqrt(
        Math.pow(touchesEvents[1].clientX - touchesEvents[0].clientX, 2) +
          Math.pow(touchesEvents[1].clientY - touchesEvents[0].clientY, 2)
      );
    }
  };

  displayObject.pinchMove = (event) => {
    // ピンチインアウトを実現
    const touchesEvents = event.nativeEvent.touches;
    if (touchesEvents && touchesEvents.length > 1 && touchStartInterval) {
      const touchInterval = Math.sqrt(
        Math.pow(touchesEvents[1].clientX - touchesEvents[0].clientX, 2) +
          Math.pow(touchesEvents[1].clientY - touchesEvents[0].clientY, 2)
      );
      const targetScale = (zoomStartScale * touchInterval) / touchStartInterval;
      scaleFunction(displayObject, targetScale);
    }
  };
};

/**
 * displayObjectにピンチイン・ピンチアウトイベントを登録する関数
 * @param  {} stage
 * @param  {} displayObject
 * @param  {} scaleFunction
 */
export const addDisplayObjectPinchEvent = (
  stage,
  displayObject,
  scaleFunction
) => {
  createDisplayObjectPinchEvent(displayObject, scaleFunction);

  displayObject.addEventListener("mousedown", displayObject.pinchStart);
  displayObject.addEventListener("pressmove", displayObject.pinchMove);
};

/**
 * displayObjectに各種タッチイベントを登録する関数
 * @param  {} stage
 * @param  {} displayObject
 */
export const addDisplayObjectEvent = (stage, displayObject) => {
  // 別のオブジェクトにフォーカスが当たっているかもしれないので外す
  focusOff();

  let dragPointX;
  let dragPointY;

  displayObject.handleDown = (event) => {
    dragPointX = stage.mouseX - displayObject.x;
    dragPointY = stage.mouseY - displayObject.y;
    displayObject.alpha = 0.5;
    hideButtons();
  };

  displayObject.handleMove = (event) => {
    displayObject.x = stage.mouseX - dragPointX;
    displayObject.y = stage.mouseY - dragPointY;
  };

  displayObject.handleUp = (event) => {
    // タッチが残っていたら処理しない
    const touchesEvents = event.nativeEvent.touches;
    if (touchesEvents && touchesEvents.length > 0) {
      return;
    }
    // マルチタッチして途中で離した作成素材が半透明のままかもしれないので、すべてのアルファを変える
    displayObjectsContainer.children.forEach((displayObject) => {
      displayObject.alpha = 1.0;
    });
    focusOn(displayObject);
  };

  // タッチ時にフォーカス
  displayObject.focus = (event) => {
    focusOff();
    focusOn(displayObject);
  };
  displayObject.addEventListener("mousedown", displayObject.focus);

  displayObject.addEventListener("mousedown", displayObject.handleDown);
  displayObject.addEventListener("pressmove", displayObject.handleMove);
  displayObject.addEventListener("pressup", displayObject.handleUp);

  // ピンチイン・ピンチアウトのイベントを登録
  addDisplayObjectPinchEvent(stage, displayObject, setScale);

  // 色変更が効くようにフォーカスする
  focusOn(displayObject, false);
};

/**
 * operationButtonsContainerにイベント登録する関数
 * @param  {} type
 * @param  {} func
 */
const addOperationButtonsContainerEvent = (type, func) => {
  if (func) {
    buttonFrameShape.addEventListener(type, func);
  }
};

/**
 * displayObjectにフォーカスする関数
 * @param  {} targetDisplayObject
 * @param  {} isShowButton
 */
export const focusOn = (
  targetDisplayObject = undefined,
  isShowButton = true
) => {
  // displayObjectが真ん中に表示される位置までずらす
  // const displayObjectsContainer = stage.getChildByName('displayObjectsContainer');
  // displayObjectsContainer.y = (stage.getBounds().width / 2) - focusObject.localToGlobal(focusObject.getBounds().width / 2, focusObject.getBounds().height / 2).y;

  // 操作ボタン群を描画する
  if (targetDisplayObject !== undefined) {
    focusObject = targetDisplayObject;
  }
  if (focusObject === undefined) {
    return;
  }
  if (isShowButton) {
    createButtonFrameShape();
    showButtons();
    // operationButtonsContainerを操作したときのイベントにもdisplayObjectデフォルトのイベントを登録しておく
    addOperationButtonsContainerEvent("mousedown", focusObject.handleDown);
    addOperationButtonsContainerEvent("pressmove", focusObject.handleMove);
    addOperationButtonsContainerEvent("pressup", focusObject.handleUp);
    addOperationButtonsContainerEvent("mousedown", focusObject.pinchStart);
    addOperationButtonsContainerEvent("pressmove", focusObject.pinchMove);
  }

  return;
};

/**
 * buttonFrameShapeを作り直す関数
 */
const createButtonFrameShape = () => {
  if (buttonFrameShape) {
    operationButtonsContainer.removeChild(buttonFrameShape);
  }
  // ボタンを表示する枠の生成
  buttonFrameShape = new createjs.Shape();
  buttonFrameShape.name = "buttonFrameShape";
  operationButtonsContainer.addChildAt(buttonFrameShape, 0);
};

/**
 * displayObjectからフォーカスを外す関数
 */
export const focusOff = () => {
  // 背景画面を押下した際に操作ボタン群を非表示にする
  hideButtons();

  if (focusObject) {
    // イベントを削除するためにbuttonFrameShapeの作り直し
    createButtonFrameShape();
  }

  // 対象の作成素材を外しておく
  focusObject = undefined;

  // displayObjectsContainerの位置を戻す
  // displayObjectsContainer.y = 0;
  return;
};

/**
 * focusObjectを次のdisplayObjectに変更する関数
 */
export const focusChange = () => {
  // focusしているオブジェクトが無い場合はz-indexが一番大きいものを選択する
  if (!focusObject) {
    // 作成素材が無い場合は何もしない
    if (displayObjectsContainer.numChildren <= 1) {
      return;
    }
    focusOff();
    focusOn(
      displayObjectsContainer.getChildAt(
        displayObjectsContainer.numChildren - 1
      ),
      true
    );
    return;
  }
  const z = displayObjectsContainer.getChildIndex(focusObject);
  let nextZ = z + 1;
  if (nextZ >= displayObjectsContainer.numChildren) {
    nextZ -= displayObjectsContainer.numChildren;
  }
  // 0はベースイラストなので外す
  if (nextZ === 0) {
    nextZ += 1;
  }
  const neighborDisplayObject = displayObjectsContainer.getChildAt(nextZ);
  focusOff();
  focusOn(neighborDisplayObject, true);
  return;
};

/**
 * displayObjectを削除し、その元あったz-indexを返す関数
 */
const deleteDisplayObject = () => {
  const z = displayObjectsContainer.getChildIndex(focusObject);
  displayObjectsContainer.removeChild(focusObject);
  focusOff();
  return z;
};

/**
 * 選択中の作成素材をプレビュー作成素材と入れ替える関数
 */
const swapPreviewMaterial = () => {
  const z = deleteDisplayObject();
  previewContainer.children.forEach((focusObject) => {
    displayObjectsContainer.addChildAt(focusObject, z);
    addDisplayObjectEvent(stage, focusObject);
  });
  // プレビューの削除
  previewContainer.removeAllChildren();
};

/**
 * END======================================================================================================================
 */
